import { useState } from "react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "../ui";
import { FormLabel } from "@/components";
import { Loader } from "../Loader";
import { toast } from "@/components/Toast";
import { X } from "lucide-react";
import { DeleteDialogProps } from "./types";

const DeleteDialog = ({
  open,
  onClose,
  onConfirm,
  contents,
  isLoading,
  optionalReason = false
}: DeleteDialogProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState("");

  const onYes = () => {
    if (contents.length - 1 == currentStep) {
      setShowReason(true);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const onSubmit = () => {
    if (!optionalReason && reason.trim()?.length < 20) {
      toast.error("Please enter a reason with at least 20 characters");
      return;
    }
    setCurrentStep(0);
    setShowReason(false);
    setReason("");
    onConfirm(reason);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-screen-2xl p-6 rounded-2xl shadow-xl"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-primary">
              {showReason ? "Reason for Deletion" : "Confirmation"}
              <div className="h-1 w-[60%] bg-primary rounded-full" />
            </DialogTitle>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                // onClick={onCloseClick}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
              >
                <X className="h-5 w-5 stroke-[5px]" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <DialogDescription className="text-gray-600 mt-2">
          {showReason ? (
            <div className="relative flex flex-col items-start gap-1 mt-5">
              <FormLabel>
                Description<span className="text-red-700">{optionalReason ? "" : "*"}</span>
              </FormLabel>
              <Textarea
                onChange={(e) => setReason(e.target.value)}
                value={reason}
                rows={10}
                placeholder="Please enter"
              />
            </div>
          ) : (
            contents?.[currentStep]
          )}
        </DialogDescription>

        <div className="flex justify-end gap-4 mt-6">
          {showReason ? (
            <Button
              variant="default"
              className="bg-red-500 hover:bg-red-500"
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading && <Loader variant="secondary" />} Delete
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                No
              </Button>
              <Button
                variant="default"
                className="bg-primary hover:bg-primary"
                onClick={onYes}
              >
                Yes
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteDialog;
